import React, { useCallback, useState, type FC } from 'react';
import { useDisclosure } from '@chakra-ui/react';
import {
  type UiHStackProps,
  UiStack,
  UiText,
  uiStyles,
  UiHStack, UiIconPlusCircle, UiButton, UiIconCaretLeft, UiIconCaretRight,
} from '@/lib/ui';
import AttendeeForm from './AttendeeForm';
import LayoutFooter from '@/registration/component/Register/Layout/Footer';
import Attendee from './Attendee';
import { tempRegistrationsQueryKey, loadTempRegistrationsByGroupRegistration, type TempRegistration } from '@/api/registration';
import { useRegisterRoute } from '@/registration/hook/useRegisterRoute';
import { useTenantApi } from '@/account/hook/useTenantApi';
import { useMutation, useQuery } from '@tanstack/react-query';
import { StandardSpinner } from '@/base/QueryContainer/QueryContainer';
import { useRegisterGroupSteps } from '@/registration/hook/useRegisterGroupSteps';
import { useGroupRegistrationQuery } from '@/registration/hook/useGroupRegistrationQuery';
import { useModal } from '@/registration/hook/useModal';
import { type GroupRegistration, type GroupRegistrationValidAttendeeRequest, validAttendeeGroupRegistration } from '@/api/registration/groupRegistration';
import { type ApiResponseSingle } from '@/api/tenantClient';
import CheckValidAttendee from './CheckValidAttendee';
import BaseThemeButton from '@/base/ThemeButton';
import BrandFooter from '@/registration/component/Register/Layout/BrandFooter';

export interface AttendeeListProps extends UiHStackProps {
}
const AttendeeList: FC<AttendeeListProps> = () => {
  const { selectedData, isOpen, onOpen, onClose } = useModal<TempRegistration>();
  const [errors, setErrors] = useState<string[]>([]);

  const { eventId } = useRegisterRoute();
  const { createTenantApiRequest, isLoading: isApiPreparing } = useTenantApi();
  const groupRegistrationQuery = useGroupRegistrationQuery(eventId);
  const groupRegistrationId = groupRegistrationQuery?.data?.id;
  const { toNextStep, toPreviousStep } = useRegisterGroupSteps();
  const attendeeCategory = groupRegistrationQuery.data?.attendeeCategory;
  const tempRegistrationsLength = groupRegistrationQuery.data?.tempRegistrations.length ?? 0;
  const minimumAttendees = Number(attendeeCategory?.minimumAttendees);
  const maximumAttendees = Number(attendeeCategory?.maximumAttendees);
  const maximumAttendeesUnset = attendeeCategory?.maximumAttendees === null;
  const minimumAttendeesUnset = attendeeCategory?.minimumAttendees === null;
  const maximumAttendeesValid = (maximumAttendeesUnset || maximumAttendees > tempRegistrationsLength);

  const { mutateAsync: validAttendeeGroupRegistrationMutate, isLoading: isValidAttendeeGroupRegistration } = useMutation<ApiResponseSingle<GroupRegistration>, Error, GroupRegistrationValidAttendeeRequest>({
    mutationFn: async (data: GroupRegistrationValidAttendeeRequest) => {
      return await validAttendeeGroupRegistration(createTenantApiRequest)(data);
    },
    onSuccess: async (data: ApiResponseSingle<GroupRegistration>) => {
      if (data.errors && data.errors.length > 0) {
        setErrors(data.errors);
        onToggleConfirmDeletion();
      } else { toNextStep(); }
    },
    onError: (registrationError: any) => {
      throw registrationError;
    }
  });
  const { isOpen: confirmDeletion, onToggle: onToggleConfirmDeletion } = useDisclosure();

  const attendees = useQuery(
    [tempRegistrationsQueryKey, { eventId, groupRegistrationId }],
    async () => {
      if (!groupRegistrationId) {
        return [];
      }
      const result = await loadTempRegistrationsByGroupRegistration(createTenantApiRequest)({ groupRegistrationId });
      return result.items;
    },
    {
      enabled: !isApiPreparing && !!groupRegistrationId,
    }
  );

  if (attendees.isLoading) {
    return <StandardSpinner />;
  }

  if (!attendees.data || !groupRegistrationId) {
    return null;
  }

  return (
    <UiStack spacing={4} py={2}>
      {attendees?.data?.map((attendee) => {
        return (
          <Attendee
            key={attendee.id}
            attendee={attendee}
            groupRegistrationId={groupRegistrationId}
            onEdit={onOpen}
          />
        );
      })}
      <UiHStack justifyContent={'flex-start'} flexGrow={1} spacing={4} pt={2}>
        <BaseThemeButton
          theme={'primary'}
          onClick={() => { return onOpen(); }}
          isDisabled={ !maximumAttendeesValid }
          leftIcon={(<UiIconPlusCircle size={'xl'} color={'primary.500'}/>)}
        >
          Add attendee
        </BaseThemeButton>
      </UiHStack>
      <AttendeeForm
        key={selectedData?.id}
        defaultAttendee={selectedData}
        isVisible={isOpen}
        onClose={onClose}
        onSaveSuccess={() => {}}
        groupRegistrationId={groupRegistrationId}
      />
      <LayoutFooter isGroup>
        <UiHStack justifyContent={'space-between'} flexGrow={1}>
          {/*<UiButton*/}
          {/*  // px={4}*/}
          {/*  size={'lg'}*/}
          {/*  colorScheme={'primary'}*/}
          {/*  onClick={toPreviousStep}*/}
          {/*  variant={'ghost'}*/}
          {/*  fontSize={'lg'}*/}
          {/*  leftIcon={(*/}
          {/*    <UiIconCaretLeft color={'primary.800'} size={'xl'} weight={'bold'}/>*/}
          {/*  )}*/}
          {/*>*/}
          {/*  Previous*/}
          {/*</UiButton>*/}
          {/*<UiButton*/}
          {/*  // px={4}*/}
          {/*  size={'lg'}*/}
          {/*  colorScheme={'primary'}*/}
          {/*  onClick={async () => await validAttendeeGroupRegistrationMutate({id: groupRegistrationId})}*/}
          {/*  isDisabled={isValidAttendeeGroupRegistration}*/}
          {/*  variant={'ghost'}*/}
          {/*  fontSize={'lg'}*/}
          {/*  rightIcon={(*/}
          {/*    <UiIconCaretRight color={'primary.800'} size={'xl'} weight={'bold'}/>*/}
          {/*  )}*/}
          {/*>*/}
          {/*  Next*/}
          {/*</UiButton>*/}

          <BaseThemeButton
            theme={'gray'}
            onClick={toPreviousStep}
            leftIcon={(
              <UiIconCaretLeft color={'primary.800'} size={'lg'} weight={'bold'}/>
            )}
          >
            Prev
          </BaseThemeButton>
          <BrandFooter/>
          <BaseThemeButton
            theme={'primary'}
            onClick={async () => await validAttendeeGroupRegistrationMutate({id: groupRegistrationId})}
            isDisabled={isValidAttendeeGroupRegistration}
            rightIcon={(
              <UiIconCaretRight color={'primary.800'} size={'lg'} weight={'bold'}/>
            )}
          >
            Next
          </BaseThemeButton>
        </UiHStack>
      </LayoutFooter>
      <CheckValidAttendee isOpen={confirmDeletion} onClose={onToggleConfirmDeletion} messsages={errors} />
    </UiStack>
  );
};

export default AttendeeList;
